import { FormEvent, ReactNode, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect } from '@/components/ui/native-select'
import { Textarea } from '@/components/ui/textarea'
import { PRIORITY_LEVELS } from '@/lib/tasks-board'
import { TaskFormValues, validateTask } from '@/lib/task-form'
import { TASK_CATEGORY_LABELS, type TaskCategory } from '@/types/tasks'
import type { TeamMember } from '@/services/team-service'
import type { Carteira } from '@/services/task-board-service'
import { CasePicker } from './CasePicker'
import { TaskAgendaFields } from './TaskAgendaFields'

interface TaskFormProps {
  initial: TaskFormValues
  members: TeamMember[]
  carteiras: Carteira[]
  mode: 'create' | 'edit'
  saving: boolean
  onSubmit: (values: TaskFormValues) => void
  onCancel?: () => void
}

function Field({ id, label, error, children }: { id: string; label: string; error?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

/** Campos da tarefa (modelo Notion): tarefa, responsável, prazo, prioridade, processo, carteira, anexo, notas. */
export function TaskForm({ initial, members, carteiras, mode, saving, onSubmit, onCancel }: TaskFormProps) {
  const [values, setValues] = useState<TaskFormValues>(initial)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const set = (patch: Partial<TaskFormValues>) => setValues((v) => ({ ...v, ...patch }))

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const found = validateTask(values)
    setErrors(found)
    if (Object.keys(found).length === 0) onSubmit(values)
  }

  return (
    <form className="flex flex-col gap-3" onSubmit={submit} noValidate aria-label={mode === 'create' ? 'Nova tarefa' : 'Editar tarefa'}>
      <Field id="task-title" label="Tarefa" error={errors.title}>
        <Input id="task-title" value={values.title} placeholder="Ex.: Protocolar contestação" onChange={(e) => set({ title: e.target.value })} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field id="task-responsavel" label="Responsável">
          <NativeSelect id="task-responsavel" value={values.assigned_user_id} onChange={(e) => set({ assigned_user_id: e.target.value })}>
            {members.map((m) => <option key={m.id} value={m.id}>{m.fullName || m.email}</option>)}
          </NativeSelect>
        </Field>
        <Field id="task-prioridade" label="Prioridade">
          <NativeSelect id="task-prioridade" value={values.prioridade} onChange={(e) => set({ prioridade: e.target.value as TaskFormValues['prioridade'] })}>
            {PRIORITY_LEVELS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </NativeSelect>
        </Field>
        <Field id="task-prazo-fatal" label="Prazo fatal">
          <Input id="task-prazo-fatal" type="date" value={values.prazo_fatal} onChange={(e) => set({ prazo_fatal: e.target.value })} />
        </Field>
        <Field id="task-prazo-interno" label="Prazo interno" error={errors.prazo_interno}>
          <Input id="task-prazo-interno" type="date" value={values.prazo_interno} onChange={(e) => set({ prazo_interno: e.target.value })} />
        </Field>
      </div>
      <p className="-mt-1 text-xs text-muted-foreground">
        Com só o prazo fatal, o interno é calculado no servidor (2 dias úteis antes, calendário do tribunal).
      </p>

      <Field id="task-processo" label="Nº do processo">
        <CasePicker value={values.caseLink} disabled={mode === 'edit'} onChange={(caseLink) => set({ caseLink })} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field id="task-categoria" label="Tipo">
          <NativeSelect id="task-categoria" value={values.category} onChange={(e) => set({ category: e.target.value as TaskCategory })}>
            {Object.entries(TASK_CATEGORY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </NativeSelect>
        </Field>
        <Field id="task-carteira" label="Carteira">
          <NativeSelect id="task-carteira" value={values.carteira_id} onChange={(e) => set({ carteira_id: e.target.value })}>
            <option value="">Sem carteira</option>
            {carteiras.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </NativeSelect>
        </Field>
      </div>

      <Field id="task-anexo" label="Anexo (link)" error={errors.attachment_url}>
        <Input id="task-anexo" type="url" placeholder="https://…" value={values.attachment_url} onChange={(e) => set({ attachment_url: e.target.value })} />
      </Field>

      <Field id="task-descricao" label="Notas">
        <Textarea id="task-descricao" rows={4} value={values.description} onChange={(e) => set({ description: e.target.value })} />
      </Field>

      {mode === 'create' && (
        <TaskAgendaFields value={values.agenda} error={errors.agenda} onChange={(agenda) => set({ agenda })} />
      )}

      <div className="flex justify-end gap-2 pt-1">
        {onCancel && <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>}
        <Button type="submit" disabled={saving}>{mode === 'create' ? 'Criar tarefa' : 'Salvar alterações'}</Button>
      </div>
    </form>
  )
}
